// src/modules/units/tyres/views/archive/archivedtyres.js
//
// Orchestrator for Records → Archived tyres (administrator only): fetch a page of
// archived tyres from GET /api/tyre/deleted, render via the pure view, and restore
// one with POST /api/tyre/{id}/restore. A thin fetch-and-swap shell — all markup
// lives in archivedtyres.view.js.
//
// Fetch / filter split:
//   - SEARCH is server-side (`search` is a documented parameter).
//   - YARD is client-side. The endpoint documents only limit / offset / search /
//     externalTyreId, and an undocumented ?yardId= would be a silent no-op (the
//     imeiNumber/unitId lesson). Every row carries its own `yardId`, so the list
//     sweeps the archived set (fetchAllArchivedTyres — explicit positive limit per
//     page) and filters on that. Because the filter is client-side, PAGING is too:
//     server pages would come back thinned out by the filter.
//   - A yard change (SCOPE_CHANGED) only re-filters what is already in hand — no
//     refetch. A write (CHANGED) or a new search refetches.
// When the backend documents yardId on this endpoint, the sweep can go back to a
// server-paged query and the filter moves into the request.
//
// Restore goes through tyres.api.js, which emits TYRE_EVENTS.CHANGED; this view
// refetches off that event like every other screen, so the restored row leaves
// this list and appears in the register/dashboard through the one shared cycle.

import { fetchAllArchivedTyres, restoreTyre } from '../tyres.api.js';
import { getSelectedYard, getSelectedYardId } from '../../shared/tyre-scope.js';
import { canArchiveTyres } from '../../shared/tyre-access.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import {
  shellHTML, rowHTML, stateRowHTML, noticeHTML, summaryText,
  filterArchivedByYard, sortArchivedForDisplay, clientPage
} from './archivedtyres.view.js';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

/**
 * @param {HTMLElement} viewEl controller view slot (emptied first)
 * @param {object} ctx
 * @param {(tyre:object)=>void} [ctx.onOpenTyre] open a just-restored tyre's detail
 * @returns {() => void} teardown
 */
export function renderArchivedTyres(viewEl, ctx = {}) {
  let disposed = false;
  let debounceId = null;
  let loadSeq = 0;            // drops out-of-order responses (fast typing / paging)
  let abort = null;

  const state = {
    search: '',
    offset: 0,
    all: null,                // every archived tyre matching the search (null = not loaded)
    capped: false,            // the sweep hit its page seatbelt
    rows: [],                 // the visible page
    restoringId: null,
    notice: null              // { kind: 'ok'|'error'|'warn', text, openId? }
  };

  const yardLabel = () => getSelectedYard()?.name || 'All yards';

  const root = el(shellHTML({
    tbodyInner: stateRowHTML('loading', 'Loading archived tyres…'),
    yardName: yardLabel()
  }));
  const scopeEl = root.querySelector('[data-scope]');
  const rowsEl = root.querySelector('[data-rows]');
  const noticeEl = root.querySelector('[data-notice]');
  const summaryEl = root.querySelector('[data-summary]');
  const prevBtn = root.querySelector('[data-prev]');
  const nextBtn = root.querySelector('[data-next]');
  const searchEl = root.querySelector('[data-search]');

  viewEl.replaceChildren(root);

  const canRestore = canArchiveTyres();

  function renderNotice() { noticeEl.innerHTML = noticeHTML(state.notice); }
  function setNotice(notice) { state.notice = notice; renderNotice(); }

  function renderRows() {
    rowsEl.innerHTML = state.rows
      .map((t) => rowHTML(t, { restoringId: state.restoringId, canRestore }))
      .join('');
  }

  function renderFooter(info) {
    summaryEl.textContent = info ? summaryText(info) : '';
    prevBtn.disabled = !info || !info.hasPrev || state.restoringId !== null;
    nextBtn.disabled = !info || !info.hasNext || state.restoringId !== null;
  }

  // Filter the loaded set to the selected yard, slice the current page, paint.
  // Runs on every fetch, page turn and yard change — never fetches.
  function renderPage() {
    if (scopeEl) scopeEl.textContent = yardLabel();
    if (!state.all) return;

    const scoped = filterArchivedByYard(state.all, getSelectedYardId());
    const page = clientPage(scoped, state.offset, PAGE_SIZE);
    state.offset = page.offset;
    state.rows = page.rows;

    if (!page.rows.length) {
      const q = state.search.trim();
      const inYard = getSelectedYardId() !== null ? ` in ${yardLabel()}` : '';
      rowsEl.innerHTML = q
        ? stateRowHTML('empty', `No archived tyre${inYard} matches “${q}”.`, { clear: true })
        : stateRowHTML('empty', inYard
          ? `No archived tyres${inYard}. Choose All yards in the header to see every yard.`
          : 'No archived tyres. Tyres an administrator archives from the Edit tyre screen appear here.');
      renderFooter(null);
      return;
    }

    renderRows();
    renderFooter(page);
  }

  async function load() {
    // Front-end guard only; the server returns 403 for non-administrators anyway.
    if (!canRestore) {
      rowsEl.innerHTML = stateRowHTML('denied', 'Administrator access is required to view archived tyres.');
      renderFooter(null);
      return;
    }

    const seq = ++loadSeq;
    abort?.abort();
    abort = typeof AbortController !== 'undefined' ? new AbortController() : null;

    rowsEl.innerHTML = stateRowHTML('loading', 'Loading archived tyres…');
    renderFooter(null);

    let res;
    try {
      res = await fetchAllArchivedTyres(
        { search: state.search.trim() || undefined },
        { signal: abort?.signal }
      );
    } catch (err) {
      if (disposed || seq !== loadSeq || err?.name === 'AbortError') return;
      rowsEl.innerHTML = stateRowHTML('error', 'Could not load archived tyres. Check your connection and try again.', { retry: true });
      return;
    }
    if (disposed || seq !== loadSeq) return;

    if (!res.success) {
      const msg = res.status === 403
        ? 'Administrator access is required to view archived tyres.'
        : res.status === 401
          ? 'Your session has expired. Please sign in again.'
          : (res.message || 'Could not load archived tyres.');
      rowsEl.innerHTML = stateRowHTML('error', msg, { retry: res.status !== 403 && res.status !== 401 });
      return;
    }

    state.all = sortArchivedForDisplay(res.data?.results);
    state.capped = !!res.data?.capped;
    // Say so rather than silently showing a partial list — but never overwrite a
    // restore notice the user has not seen yet.
    if (state.capped && (!state.notice || state.notice.kind !== 'ok')) {
      setNotice({ kind: 'warn', text: `Showing the first ${state.all.length} archived tyres only — search by tyre ID to find others.` });
    }
    renderPage();
  }

  async function restore(id) {
    if (!id || state.restoringId !== null) return;
    if (!canArchiveTyres()) {
      setNotice({ kind: 'error', text: 'Administrator access is required to restore a tyre.' });
      return;
    }

    state.restoringId = id;
    setNotice(null);
    renderPage();

    let res;
    try {
      res = await restoreTyre(id);
    } catch (_) {
      res = { success: false, status: 0, message: 'Could not restore the tyre. Check your connection and try again.' };
    }
    if (disposed) return;

    state.restoringId = null;

    if (res.success) {
      // The list itself refreshes off the CHANGED event restoreTyre() emitted;
      // this only says what happened and offers the obvious next step.
      setNotice({
        kind: 'ok',
        text: `Tyre ${id} restored to the register with its history and last status. No sensor was re-fitted.`,
        openId: id
      });
      return;
    }

    if (res.status === 404) {
      // Someone else restored it (or it vanished) — say so and refresh.
      setNotice({ kind: 'warn', text: `Tyre ${id} is no longer archived — it may already have been restored. The list has been refreshed.` });
      load();
      return;
    }

    setNotice({
      kind: 'error',
      text: res.status === 403
        ? 'Administrator access to this tyre’s customer is required to restore it.'
        : res.status === 401
          ? 'Your session has expired. Please sign in again.'
          : (res.message || `Could not restore tyre ${id}.`)
    });
    renderPage();
  }

  // ---- wiring ----
  searchEl.addEventListener('input', () => {
    clearTimeout(debounceId);
    debounceId = setTimeout(() => {
      state.search = searchEl.value;
      state.offset = 0;
      load();
    }, SEARCH_DEBOUNCE_MS);
  });

  // Paging is local: the whole (yard-filtered) set is already in hand.
  prevBtn.addEventListener('click', () => {
    if (state.offset === 0) return;
    state.offset = Math.max(0, state.offset - PAGE_SIZE);
    renderPage();
  });
  nextBtn.addEventListener('click', () => {
    state.offset += PAGE_SIZE;
    renderPage();
  });

  rowsEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-retry]')) { load(); return; }
    if (e.target.closest('[data-clear]')) {
      searchEl.value = '';
      state.search = '';
      state.offset = 0;
      load();
      searchEl.focus();
      return;
    }
    const btn = e.target.closest('[data-restore]');
    if (btn && !btn.disabled) restore(btn.dataset.restore);
  });

  noticeEl.addEventListener('click', (e) => {
    const open = e.target.closest('[data-open-restored]');
    if (open && typeof ctx.onOpenTyre === 'function') {
      ctx.onOpenTyre({ externalTyreId: open.dataset.openRestored });
    }
  });

  load();

  // Any tyre write (a restore here, an archive from another screen or session's
  // edit form) can change this list. Keep the notice; it describes the write
  // that caused the refresh.
  const offChanged = onTyreEvent(TYRE_EVENTS.CHANGED, load);
  // The header yard selector moved: re-filter what is already loaded, from page 1.
  const offScope = onTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, () => { state.offset = 0; renderPage(); });

  return () => {
    disposed = true;
    clearTimeout(debounceId);
    abort?.abort();
    offChanged();
    offScope();
  };
}
